import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiMail } from 'react-icons/fi';
import { moveEarlyGradeStudent } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { TRACK_NAMES } from '../../types/constants';
import {
  canUseEarlyGradeMove,
  EARLY_GRADE_TRACKS,
  EarlyGrade
} from '../../policies/earlyGradeMovePolicy';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

interface MoveResult {
  success: boolean;
  message: string;
  enrollmentResult?: {
    success: boolean;
    message: string;
    transferredFrom?: string;
    transferredTo?: string;
  };
}

export function EarlyGradeMovePage() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [grade, setGrade] = useState<EarlyGrade>('-1');
  const [focusTrack, setFocusTrack] = useState<string>(EARLY_GRADE_TRACKS['-1'][0]);
  const [isMoving, setIsMoving] = useState(false);
  const [result, setResult] = useState<MoveResult | null>(null);
  const hasAccess = useMemo(
    () => canUseEarlyGradeMove(user?.email, user?.role),
    [user?.email, user?.role]
  );

  const changeGrade = (nextGrade: EarlyGrade) => {
    setGrade(nextGrade);
    setFocusTrack(EARLY_GRADE_TRACKS[nextGrade][0]);
    setResult(null);
  };

  const submit = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setResult({ success: false, message: 'Enter a student email.' });
      return;
    }

    const gradeLabel = grade === '-1' ? 'Pre-K' : 'Kindergarten';
    if (!window.confirm(`Move ${normalizedEmail} to ${gradeLabel} at ${focusTrack}?`)) return;

    setIsMoving(true);
    setResult(null);
    try {
      const response = await moveEarlyGradeStudent(normalizedEmail, Number(grade) as -1 | 0, focusTrack);
      setResult(response);
      if (response.success) setEmail('');
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to move student.'
      });
    } finally {
      setIsMoving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold text-red-600">Access denied</h1>
            <p className="mt-2 text-gray-600">This workflow is limited to approved early-grade curriculum operators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Link to="/" className="text-blue-600 hover:text-blue-800">← Back to AlphaNumbers</Link>
        <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-5 px-6 text-white">
            <h1 className="text-2xl font-semibold">Move Pre-K / Kindergarten Student</h1>
            <p className="text-sm text-blue-100 mt-1">This workflow cannot move students into Grade 1 or above.</p>
          </div>
          <CardContent className="p-6 space-y-5">
            <div>
              <label htmlFor="early-grade-email" className="block text-sm font-medium text-gray-700 mb-2">Student email</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="early-grade-email"
                  type="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="student@example.com"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="early-grade" className="block text-sm font-medium text-gray-700 mb-2">Destination grade</label>
                <select
                  id="early-grade"
                  value={grade}
                  onChange={event => changeGrade(event.target.value as EarlyGrade)}
                  className="w-full rounded-md border border-gray-300 py-2 px-3"
                >
                  <option value="-1">Pre-K</option>
                  <option value="0">Kindergarten</option>
                </select>
              </div>
              <div>
                <label htmlFor="early-grade-track" className="block text-sm font-medium text-gray-700 mb-2">Starting track</label>
                <select
                  id="early-grade-track"
                  value={focusTrack}
                  onChange={event => setFocusTrack(event.target.value)}
                  className="w-full rounded-md border border-gray-300 py-2 px-3"
                >
                  {EARLY_GRADE_TRACKS[grade].map(track => (
                    <option key={track} value={track}>{TRACK_NAMES[track]} ({track})</option>
                  ))}
                </select>
              </div>
            </div>

            {result && (
              <div className={`p-4 rounded-lg ${result.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                <div className="font-medium">{result.message}</div>
                {result.enrollmentResult && <div className="text-sm mt-2">OneRoster: {result.enrollmentResult.message}</div>}
              </div>
            )}

            <Button
              onClick={submit}
              disabled={isMoving || !email.trim()}
              className="mx-auto flex items-center gap-2"
            >
              <FiArrowRight />
              {isMoving ? 'Moving…' : 'Move student'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
